import store from "../Store";
import { locationSuccess } from "../Store/Slices/locationSlice";

// The gateway's audit trail records, per login / MPIN check / re-auth, where the request came from.
// IP and User-Agent it works out itself; location and device model can only come from here, as the
// headers X-Latitude, X-Longitude and X-Device-Model. All three are best-effort: a missing header is
// stored as NULL, so nothing in this file may ever block or fail the real request.

const AUDITED_PATHS = [
  "/staff/onboard/signin",
  "/staff/onboard/validate-mpin",
  "/auth/reauth",
  "/auth/device/verify",
  "/auth/device/remove",          // these two finish a sign-in too (and register the new device), so they carry the device number + fingerprint
  "/auth/device/end-session",
  "/auth/mpin/verify",
  "/auth/login",
];

export function isAuditedUrl(url) {
  return !!url && AUDITED_PATHS.some((p) => url.includes(p));
}

const GEO_WAIT_MS = 3000;
const GEO_MAX_AGE_MS = 5 * 60 * 1000;

function askBrowserForPosition(waitMs) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const giveUp = setTimeout(() => resolve(null), waitMs);
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(giveUp);
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          clearTimeout(giveUp);
          resolve(null);
        },
        { timeout: waitMs, maximumAge: GEO_MAX_AGE_MS }
      );
    } catch {
      clearTimeout(giveUp);
      resolve(null);
    }
  });
}

// Store first (RetailerDasboard fills it and it survives reloads); otherwise ask the browser, but never
// wait longer than waitMs (a permission prompt nobody answers must not hold the login hostage).
export async function getLocation(waitMs = GEO_WAIT_MS) {
  const { lat, lng } = store.getState().location || {};
  if (lat != null && lng != null) return { lat, lng };
  const pos = await askBrowserForPosition(waitMs);
  if (pos) store.dispatch(locationSuccess(pos));
  return pos;
}

let cachedModel; // undefined = not looked up yet, null = looked up, none available

export function resetDeviceModelCacheForTests() {
  cachedModel = undefined;
}

// What kind of device is this? User-Agent strings no longer carry the phone model; Chromium exposes it
// through client hints (no permission prompt) — but only Android phones have one. Everything else
// (desktops, phones that hide it) gets "<platform> desktop|mobile", e.g. "macOS desktop", so the audit
// row still says something. Safari/Firefox have no client hints: no header (the plain User-Agent the
// gateway records anyway covers them).
export async function getDeviceModel() {
  if (cachedModel !== undefined) return cachedModel;
  const hints = navigator.userAgentData;
  if (!hints) {
    cachedModel = null;
    return cachedModel;
  }
  let model = "";
  try {
    const values = hints.getHighEntropyValues ? await hints.getHighEntropyValues(["model"]) : null;
    model = (values?.model || "").trim();
  } catch {
    /* fall back to the platform below */
  }
  if (model) {
    cachedModel = model;
  } else {
    const platform = (hints.platform || "").trim();
    cachedModel = platform ? `${platform} ${hints.mobile ? "mobile" : "desktop"}` : null;
  }
  return cachedModel;
}

// A random id for this browser, kept in a cookie AND localStorage. The app calls localStorage.clear() on
// logout, so the cookie is what lets the same browser keep the same id across logins. It is made here, by the
// client: it helps to see "these logins came from one browser", it proves nothing and must not be trusted.
const BROWSER_ID_KEY = "bp_browser_id";
const BROWSER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TWO_YEARS_SECONDS = 60 * 60 * 24 * 365 * 2;

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function readStored() {
  try {
    return localStorage.getItem(BROWSER_ID_KEY);
  } catch {
    return null;
  }
}

function newUuid() {
  const c = typeof crypto !== "undefined" ? crypto : null;
  if (c?.randomUUID) return c.randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function getBrowserId() {
  let id = [readCookie(BROWSER_ID_KEY), readStored()].find((v) => v && BROWSER_ID_PATTERN.test(v));
  if (!id) id = newUuid();
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${BROWSER_ID_KEY}=${encodeURIComponent(id)}; max-age=${TWO_YEARS_SECONDS}; path=/; SameSite=Lax${secure}`;
  } catch { /* cookies blocked: localStorage below is the fallback */ }
  try { localStorage.setItem(BROWSER_ID_KEY, id); } catch { /* private mode */ }
  return id;
}

// A hash of traits that rarely change on one machine: platform, CPU cores, touch points and the GPU the browser
// reports. The gateway compares it with the one saved when the device was trusted, so a copied device secret does not
// work on another computer. Screen size and timezone are left out on purpose (a docked laptop or a trip would change
// them and cost the user an OTP). It is made here, so it is a claim, not proof. Never throws; null only if nothing works.
let cachedFingerprint;

export function resetFingerprintCacheForTests() {
  cachedFingerprint = undefined;
}

function gpuTraits() {
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    const ext = gl?.getExtension("WEBGL_debug_renderer_info");
    return ext ? `${gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)}|${gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)}` : "";
  } catch {
    return "";
  }
}

function trait(read) {
  try {
    const v = read();
    return v === undefined || v === null ? "" : String(v);
  } catch {
    return "";
  }
}

// FNV-1a with 8 different seeds -> 64 hex characters, for browsers without crypto.subtle (plain http pages).
function fallbackHash(text) {
  let out = "";
  for (let seed = 0; seed < 8; seed += 1) {
    let h = (0x811c9dc5 ^ (seed * 0x9e3779b1)) >>> 0;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    out += h.toString(16).padStart(8, "0");
  }
  return out;
}

export async function getDeviceFingerprint() {
  if (cachedFingerprint !== undefined) return cachedFingerprint;
  const text = [
    trait(() => navigator.platform),
    trait(() => navigator.hardwareConcurrency),
    trait(() => navigator.maxTouchPoints),
    gpuTraits(),
  ].join("||");
  try {
    if (typeof crypto !== "undefined" && crypto.subtle?.digest && typeof TextEncoder !== "undefined") {
      const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
      cachedFingerprint = Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
      return cachedFingerprint;
    }
  } catch {
    /* use the fallback below */
  }
  cachedFingerprint = fallbackHash(text);
  return cachedFingerprint;
}

export async function getAuditHeaders() {
  const headers = { "X-Portal": "merchant" };  // which frontend this is; the gateway stores it with the event
  try {
    headers["X-Browser-Id"] = getBrowserId();
    const [loc, model, fp] = await Promise.all([getLocation(), getDeviceModel(), getDeviceFingerprint()]);
    if (loc) {
      headers["X-Latitude"] = String(loc.lat);
      headers["X-Longitude"] = String(loc.lng);
    }
    if (model) headers["X-Device-Model"] = model;
    if (fp) headers["X-Device-Fp"] = fp;
  } catch {
    /* audit data is optional */
  }
  return headers;
}
